import { CalendarIcon, ClockIcon, DocumentTextIcon, ShoppingCartIcon, TagIcon, TicketIcon, UsersIcon } from 'assets/icons'
import { useEffect, useState } from 'react'
import { usePlace } from 'store/usePlace'
import { useWizardForm } from 'store/useWizardForm'
import FormattedMessage from 'utils/FormattedMessage'
import { formatTimeToLocale } from 'utils/formatTimeToLocale'
import { SmallProductItem } from './SmallProductItem'

export const DesktopInfoBlock = ({ className }: { className?: string }) => {
  const { place } = usePlace()
  const { wizardForm } = useWizardForm()
  const [categoryName, setCategoryName] = useState<string | undefined>('')

  useEffect(() => {
    if (wizardForm.serviceCategoryId) {
      const serviceCategory = place?.serviceCategories.find(
        category => category.id === wizardForm.serviceCategoryId
      )
      setCategoryName(serviceCategory?.name)
    }
  }, [wizardForm.serviceCategoryId])

  const formFields = wizardForm.form
    ? Object.values(wizardForm.form.pages).flatMap(page =>
        Object.values(page.fields).filter(
          f => f.type !== 'info' && (f.value !== undefined && f.value !== '' || (f.multiValue && f.multiValue.length > 0))
        )
      )
    : []

  const hasContactData =
    wizardForm.contactData.firstName ||
    wizardForm.contactData.lastName ||
    wizardForm.contactData.email ||
    wizardForm.contactData.phone ||
    wizardForm.contactData.numberOfVisitors

  const hasProducts =
    wizardForm.order && Object.values(wizardForm.order.products).some(p => p.quantity > 0)

  return (
    <div className={`hidden md:flex flex-col ${className ?? ''}`}>
      <p className='text-sm text-gray-700 dark:text-gray-100 mb-3'>
        <FormattedMessage id='bookingDetails' />
      </p>
        <div className='flex-col w-full border border-solid border-gray-300 dark:border-gray-300 rounded-lg bg-white dark:bg-custom-secondary'>
        {/* Header */}
        <div className='flex w-full p-3 bg-custom-header rounded-t-lg'>
          <div className='mr-3 items-center'>
            <TicketIcon className='w-6 h-6 stroke-gray-900 dark:stroke-gray-100' />
          </div>
          <div className='flex items-center'>
            <div className='text-sm'>
              <p className='font-medium text-gray-900 dark:text-gray-100'>
                <FormattedMessage id='visit' />
              </p>
            </div>
          </div>
        </div>

        {/* Service + category */}
        {wizardForm.serviceName && (
          <div className='flex flex-col justify-center items-start px-3 py-3'>
            <p className='text-sm text-gray-700 dark:text-gray-100 font-medium'>
              {wizardForm.serviceName}
              {categoryName && `, ${categoryName}`}
            </p>
          </div>
        )}

        {/* Queue type */}
        {wizardForm.queueType && (
          <div className='flex items-center px-3 py-3 border-t border-gray-200 dark:border-gray-500'>
            <TagIcon className='w-4 h-4 stroke-custom-accent mr-1' />
            <p className='text-xs font-semibold text-gray-700 dark:text-gray-100'>
              {wizardForm.queueType === 'schedule' ? (
                <FormattedMessage id='appointment' />
              ) : (
                <FormattedMessage id='liveQueue' />
              )}
            </p>
          </div>
        )}

        {/* Date + Time */}
        {wizardForm?.selectedTime?.label && (
          <div className='md:grid md:grid-cols-2 px-3 py-3 border-t border-gray-200 dark:border-gray-500'>
            {wizardForm?.selectedTime.fullDayName && (
              <div className='flex items-center'>
                <CalendarIcon className='w-4 h-4 stroke-custom-accent mr-1' />
                <p className='text-sm text-gray-700 dark:text-gray-100 font-normal'>
                  {wizardForm.selectedTime.fullDayName}
                </p>
              </div>
            )}
            {wizardForm?.selectedTime?.label && (
              <div className='flex items-center'>
                <ClockIcon className='w-4 h-4 stroke-custom-accent mr-1' />
                <p className='text-sm text-gray-700 dark:text-gray-100 font-normal'>
                  {formatTimeToLocale(wizardForm.selectedTime.label)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Contact data */}
        {hasContactData && (
          <div className='px-3 py-3 border-t border-gray-200 dark:border-gray-500'>
            <div className='flex items-center mb-1'>
              <UsersIcon className='w-4 h-4 stroke-custom-accent mr-1' />
              <p className='text-xs font-semibold text-gray-700 dark:text-gray-100'>
                <FormattedMessage id='contactData' />
              </p>
            </div>
            {(wizardForm.contactData.firstName || wizardForm.contactData.lastName) && (
              <p className='text-sm text-gray-700 dark:text-gray-100'>
                {[wizardForm.contactData.firstName, wizardForm.contactData.lastName].filter(Boolean).join(' ')}
              </p>
            )}
            {wizardForm.contactData.email && (
              <p className='text-sm text-gray-500 dark:text-gray-400'>{wizardForm.contactData.email}</p>
            )}
            {wizardForm.contactData.phone && (
              <p className='text-sm text-gray-500 dark:text-gray-400'>{wizardForm.contactData.phone}</p>
            )}
            {wizardForm.contactData.numberOfVisitors !== undefined && wizardForm.contactData.numberOfVisitors > 0 && (
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                <FormattedMessage id='numberOfVisitors' />: {wizardForm.contactData.numberOfVisitors}
              </p>
            )}
          </div>
        )}

        {/* Products */}
        {hasProducts && (
          <div className='px-3 py-3 border-t border-gray-200 dark:border-gray-500'>
            <div className='flex items-center mb-1'>
              <ShoppingCartIcon className='w-4 h-4 stroke-custom-accent mr-1' />
              <p className='text-xs font-semibold text-gray-700 dark:text-gray-100'>
                <FormattedMessage id='wizardForm.confirm.yourOrder' />
              </p>
            </div>
            {Object.values(wizardForm.order!.products)
              .filter(p => p.quantity > 0)
              .map(product => (
                <SmallProductItem
                  key={product.productId}
                  product={product}
                  currency={wizardForm.order!.currency}
                />
              ))}
            <p className='text-sm font-semibold text-gray-700 dark:text-gray-100 text-right mt-1'>
              <FormattedMessage id='subtotal' />: {wizardForm.order!.totalAmount.toFixed(2)} {wizardForm.order!.currency}
            </p>
          </div>
        )}

        {/* Custom form fields */}
        {formFields.length > 0 && (
          <div className='px-3 py-3 border-t border-gray-200 dark:border-gray-500'>
            <div className='flex items-center mb-1'>
              <DocumentTextIcon className='w-4 h-4 stroke-custom-accent mr-1' />
              <p className='text-xs font-semibold text-gray-700 dark:text-gray-100'>
                <FormattedMessage id='additionalInfo' />
              </p>
            </div>
            {formFields.map(field => (
              <div key={field.fieldId} className='mb-1'>
                <p className='text-xs text-gray-500 dark:text-gray-400'>{field.label}</p>
                {field.multiValue && field.multiValue.length > 0 ? (
                  <p className='text-sm text-gray-700 dark:text-gray-100'>{field.multiValue.join(', ')}</p>
                ) : (
                  <p className='text-sm text-gray-700 dark:text-gray-100'>{field.value}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
